import { useEffect, useRef } from 'react';
import { mountFeedbackWidget } from '@fibonaccitec/feedback-widget';

import { supabase } from '@/lib/supabase';
import { useFeedbackWidget } from '@/features/feedback/feedback-widget-context';

// Fibonacci Ticket Hub feedback widget. Mounted once inside the authenticated
// shell (see App.tsx), so only signed-in users can capture and report. The
// widget talks ONLY to Joshua's own feedback-relay Edge Function, which verifies
// the user JWT, resolves the pessoa server-side, and forwards to the Hub — the
// widget never reaches the Hub directly. relayUrl reuses VITE_SUPABASE_URL
// because the relay lives on Joshua's own Supabase project.
const RELAY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/feedback-relay`;

/**
 * Non-visual mount shim. The widget owns its own Shadow-DOM root. Under
 * StrictMode the effect runs setup → cleanup → setup: the cleanup tears the
 * widget down via handle(), then the second setup re-mounts cleanly and
 * re-publishes a handle bound to the live widget — so the bridge never ends up
 * holding a dead handle. (mountFeedbackWidget is also idempotent against an
 * accidental double-mount WITHOUT an intervening unmount.) The returned handle
 * (callable) tears the widget down on sign-out (when ProtectedRoute drops the
 * shell).
 *
 * HEADLESS MODE: the built-in FAB is suppressed (`showFab: false`). Reporting +
 * the resolved-ticket notice now live in the UserMenu avatar dropdown. This shim
 * PUBLISHES the widget's imperative handle and its polled unseen-resolved count
 * into the FeedbackWidget bridge; UserMenu reads them to drive the "Reportar
 * problema" / "Meus tickets" entries and the resolve badge.
 */
export default function FeedbackWidgetMount() {
  // The bridge's identity changes when the count it holds changes; reading it
  // through a ref keeps the mount effect a true mount-once (a dependency on the
  // bridge object would remount the widget on every count update). The setters
  // themselves are stable, so a ref read always reaches the live ones.
  const bridge = useFeedbackWidget();
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;

  useEffect(() => {
    const handle = mountFeedbackWidget({
      relayUrl: RELAY_URL,
      getAccessToken: async () =>
        (await supabase.auth.getSession()).data.session?.access_token ?? null,
      // No floating FAB — the UserMenu avatar dropdown is the entry point now.
      showFab: false,
      // Mirror the polled unseen-resolved count into the bridge so the avatar
      // badge tracks it live (fires on poll AND the optimistic clear-on-view).
      onUnseenCountChange: (count) => bridgeRef.current?.setUnseenCount(count),
    });
    // Publish the live handle so UserMenu can open the report / resolved view.
    bridgeRef.current?.setHandle(handle);
    return () => {
      handle(); // callable handle === unmount
      bridgeRef.current?.setHandle(null);
    };
  }, []);

  return null;
}
